import {
  ConnectionPositionPair,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'brn-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  @ViewChild('optionsContainer')
  optionsContainer!: TemplateRef<any>;

  @ViewChild('select')
  selectWrapper!: ElementRef;

  @ViewChild('suggestInput')
  suggestInput!: ElementRef;

  private overlayRef!: OverlayRef;
  private portal!: TemplatePortal<any>;

  private $value!: any;
  private openFlag = false;

  searchString = '';

  disabled = false;

  optionGroupsCollapsed: Array<any> = [];

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {}

  // -------------- Value input/output -------------

  @Input()
  set value(val) {
    this.$value = val;
    this.onChange(this.$value);
  }

  get value(): any {
    return this.$value;
  }

  @Output()
  valueChange = new EventEmitter<any>();

  // ---------------- Select config ----------------

  @Input()
  options!: any[];

  @Input()
  placeholder = 'Select...';

  @Input()
  empty: string | boolean = false;

  @Input()
  prefixIcon!: string;

  @Input()
  optionLabel!: string;

  @Input()
  optionValue!: string;

  @Input()
  optionGroups!: any[];

  @Input()
  optionGroupLabel!: string;

  @Input()
  optionGroupValue!: string;

  @Input()
  suggest: string | boolean = false;

  @Input()
  theme: 'white' | 'grey' | 'resized grey' | 'tasks--page' = 'white';

  @Input()
  maxRows = 9;

  @Input()
  scale?: number;

  @Input()
  capitalize = false;

  // ----------------- Extra styling ----------------

  get extraStyles(): { [klass: string]: any } | null {
    if (this.scale) {
      return { transform: `scale(${this.scale})`, transformOrigin: '0 0' };
    }
    return null;
  }

  // --------------- Template getters ---------------

  get valueLabel(): string {
    if (this.optionLabel && !this.optionValue) {
      return this.value[this.optionLabel];
    } else if (this.optionValue) {
      const val = this.options?.filter((item) => {
        return item[this.optionValue] === this.value;
      })[0];
      return val ? val[this.optionLabel] : 'Null';
    }

    return this.value;
  }

  getOptionLabel(option: any): any {
    if (this.optionLabel) {
      return option[this.optionLabel];
    }
    return option;
  }

  isOptionSelected(option: any): boolean {
    if (this.optionLabel && this.value && !this.optionValue) {
      return this.valueLabel === option[this.optionLabel];
    } else if (this.optionValue) {
      return option[this.optionValue] === this.value;
    }
    return option === this.value;
  }

  get suggestSearchField(): any {
    if (this.suggest && typeof this.suggest === 'string') {
      if (this.options[0][this.suggest] !== undefined) {
        return this.suggest;
      }
      return null;
    } else if (this.suggest === true && this.optionLabel) {
      return this.optionLabel;
    }
    return null;
  }

  get emptyLabel(): string {
    if (this.empty && typeof this.empty === 'string') {
      return this.empty;
    }
    return 'Leave empty...';
  }

  getGroupOptions(optionGroup: any): any {
    return this.options.filter((option) => {
      return (
        option[this.optionGroupValue] === optionGroup[this.optionGroupValue]
      );
    });
  }

  isOptionGroupCollapsed(optionGroup: any): boolean {
    return this.optionGroupsCollapsed.includes(
      optionGroup[this.optionGroupValue]
    );
  }

  // --------------- Select logic ---------------

  selectOption(option: any): void {
    if (this.optionValue) {
      this.value = option[this.optionValue];
      this.valueChange.emit(this.value);
    } else {
      this.value = option;
      this.valueChange.emit(option);
    }
    this.close();
  }

  selectEmptyOption(): any {
    this.value = null;
    this.valueChange.emit(this.value);
    this.close();
  }

  toggleOptionGroup(optionGroup: any): any {
    const i = this.optionGroupsCollapsed.indexOf(
      optionGroup[this.optionGroupValue]
    );

    if (i > -1) {
      this.optionGroupsCollapsed.splice(i, 1);
    } else {
      this.optionGroupsCollapsed.push(optionGroup[this.optionGroupValue]);
    }
  }

  // -------------- Overlay config --------------

  open(): void {
    if (this.openFlag || this.disabled) {
      return;
    }
    this.openFlag = true;
    const scrollStrategy = this.overlay.scrollStrategies.block();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.selectWrapper)
      .withPositions(this.getPositions())
      .withViewportMargin(15)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: 'overlay-popup-backdrop',
      disposeOnNavigation: true,
      maxHeight: this.maxHeight,
      minHeight: 86,
      width: this.selectWrapper.nativeElement.offsetWidth,
    });

    this.portal = new TemplatePortal(
      this.optionsContainer,
      this.viewContainerRef
    );
    this.overlayRef.attach(this.portal);
    // tslint:disable-next-line: deprecation
    this.overlayRef.backdropClick().subscribe((event: MouseEvent) => {
      this.preventPropagation(event);
      this.close();
    });
    // tslint:disable-next-line: deprecation
    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    if (this.suggest) {
      setTimeout(() => {
        this.suggestInput.nativeElement.focus();
      }, 10);
    }
  }

  private close(): void {
    this.overlayRef.detach();
    this.overlayRef.dispose();
    this.searchString = '';
    this.openFlag = false;
  }

  private getPositions(): ConnectionPositionPair[] {
    const positions = [
      new ConnectionPositionPair(
        { originX: 'start', originY: 'top' },
        { overlayX: 'start', overlayY: 'top' }
      ),
      new ConnectionPositionPair(
        { originX: 'start', originY: 'bottom' },
        { overlayX: 'start', overlayY: 'bottom' },
        0,
        0,
        'options--on-top'
      ),
    ];
    return positions;
  }

  private get maxHeight(): number {
    const rowsHeight = 39 * this.maxRows + 48;
    return rowsHeight;
  }

  // ------------ Event Propagation --------

  preventPropagation(event: Event): void {
    event.stopPropagation();
  }

  // -------- Value Accessor requirement --------
  onChange(_: any): any {}

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
